import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useI18n } from "@/context/i18n";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, Bell, Trash2, TrendingDown, ArrowRight } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, Tooltip, YAxis } from "recharts";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const goLogin = () => {
  sessionStorage.setItem("postLoginRedirect", "/watchlist");
  const redirectUrl = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const Sparkline = ({ history }) => {
  const data = (history || []).map((h, i) => ({ i, price: Math.round(h.priceEur) }));
  if (data.length < 2) return <div className="h-16 flex items-center text-xs text-[#94A3B8]">Not enough data yet</div>;
  return (
    <div className="h-16" data-testid="price-chart">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
          <YAxis hide domain={["dataMin - 10", "dataMax + 10"]} />
          <Tooltip formatter={(v) => `€${v}`} labelFormatter={() => ""} contentStyle={{ borderRadius: 10, border: "1px solid #E2E8F0", fontSize: 12 }} />
          <Line type="monotone" dataKey="price" stroke="#009E9E" strokeWidth={2.5} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default function Watchlist() {
  const { user, authLoading, formatPrice } = useApp();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [watches, setWatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [targets, setTargets] = useState({});

  const load = () => api.get("/watches").then((r) => {
    setWatches(r.data);
    const tg = {};
    r.data.forEach((w) => { tg[w.watch_id] = w.targetPriceEur ?? ""; });
    setTargets(tg);
  }).finally(() => setLoading(false));

  useEffect(() => {
    if (authLoading) return;
    if (!user) return goLogin();
    load();
    // eslint-disable-next-line
  }, [user, authLoading]);

  const remove = async (id) => {
    await api.delete(`/watches/${id}`);
    setWatches((w) => w.filter((x) => x.watch_id !== id));
    toast.success(t("remove"));
  };

  const openHotel = (w) => navigate(
    `/hotels/${w.hotelCode}?checkIn=${w.checkIn}&checkOut=${w.checkOut}&adults=${w.adults}&children=${w.children}&rooms=${w.rooms}&source=${w.source || "hbx"}`
  );

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="watchlist-page">
      <Header />
      <div className="pt-24 max-w-4xl mx-auto px-4 lg:px-8 min-h-[60vh]">
        <span className="label-caps">{t("popular")}</span>
        <h1 className="font-display text-3xl lg:text-4xl font-bold text-[#0B2038] mt-1 flex items-center gap-3">
          <Bell className="w-7 h-7 text-[#009E9E]" /> {t("watchlist")}
        </h1>
        <p className="text-[#475569] mb-6 mt-1">{t("watchlist_sub")}</p>

        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-[#009E9E]" /></div>
        ) : watches.length === 0 ? (
          <div className="bg-white border border-[#E2E8F0] rounded-2xl p-10 text-center" data-testid="watchlist-empty">
            <Bell className="w-10 h-10 text-[#475569] mx-auto mb-3" strokeWidth={1.4} />
            <p className="text-[#0B2038] font-semibold mb-1">{t("no_watches")}</p>
            <p className="text-[#475569] text-sm mb-4">{t("no_watches_sub")}</p>
            <Button onClick={() => navigate("/results/hotels?destination=IST")} className="rounded-full bg-[#009E9E] hover:bg-[#007A7A] text-white font-semibold">{t("browse_hotels")}</Button>
          </div>
        ) : (
          <div className="space-y-4">
            {watches.map((w) => {
              const start = w.startPriceEur;
              const now = w.lastPriceEur;
              const dropped = start != null && now != null && now < start - 0.5;
              return (
                <div key={w.watch_id} data-testid={`watch-${w.watch_id}`} className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden grid md:grid-cols-[160px_1fr] card-lift">
                  <button onClick={() => openHotel(w)} className="h-40 md:h-full bg-[#E2E8F0]">
                    {w.image ? <img src={w.image} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[#94A3B8] text-xs">Vaya</div>}
                  </button>
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="font-display text-lg font-extrabold text-[#0B2038] leading-tight cursor-pointer hover:text-[#009E9E]" onClick={() => openHotel(w)}>{w.hotelName}</h3>
                        <p className="text-xs text-[#475569] mt-1">{w.checkIn} → {w.checkOut}</p>
                      </div>
                      <button data-testid={`watch-remove-${w.watch_id}`} onClick={() => remove(w.watch_id)} className="text-[#94A3B8] hover:text-red-500 shrink-0"><Trash2 className="w-4.5 h-4.5" /></button>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mt-4 items-end">
                      <div>
                        <div className="flex items-end gap-3">
                          <div>
                            <p className="text-[11px] text-[#475569]">{t("current_price")}</p>
                            <p className={`font-display text-2xl font-extrabold ${dropped ? "text-[#009E9E]" : "text-[#0B2038]"}`} data-testid={`watch-price-${w.watch_id}`}>{formatPrice(now)}</p>
                          </div>
                          {dropped && <span className="flex items-center gap-1 text-[11px] font-bold text-[#009E9E] mb-1"><TrendingDown className="w-3.5 h-3.5" />{formatPrice(start - now)}</span>}
                        </div>
                        <p className="text-[11px] text-[#94A3B8] mt-0.5">{t("start_price")}: {formatPrice(start)}</p>
                      </div>
                      <Sparkline history={w.history} />
                    </div>

                    <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-[#E2E8F0]">
                      <span className="text-xs font-semibold text-[#475569]">{t("target_price")}</span>
                      <Input value={targets[w.watch_id]} onChange={(e) => setTargets({ ...targets, [w.watch_id]: e.target.value })}
                        data-testid={`watch-target-${w.watch_id}`} placeholder="€" className="h-9 w-24 rounded-lg text-sm" />
                      <Button data-testid={`watch-save-${w.watch_id}`} onClick={async () => {
                        const raw = targets[w.watch_id];
                        const val = parseFloat(raw);
                        try {
                          await api.patch(`/watches/${w.watch_id}`, { targetPriceEur: (raw === "" || isNaN(val)) ? null : val });
                          toast.success(t("target_set"));
                          load();
                        } catch (err) {
                          toast.error(err.response?.data?.detail?.[0]?.msg || "Enter a valid amount");
                        }
                      }} className="h-9 rounded-lg bg-[#0B2038] text-white text-sm font-semibold px-4">{t("save_target")}</Button>
                      <button onClick={() => openHotel(w)} className="ml-auto text-sm font-bold text-[#009E9E] flex items-center gap-1">{t("book_now")} <ArrowRight className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
